import { useEffect, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from 'recharts';
import { compactMoney, exactMoney, sumMoney, toNumber, wholeMoney } from './format';
import type { ForecastPeek, Money } from './types';

/**
 * The two drawn charts on the dashboard.
 *
 * Marks are thin and quiet on purpose — columns capped at 24px with a 4px
 * rounded end, lines at 2px with a 10% wash beneath, hairline solid grid —
 * because the numbers are the loud part. Colours come from the --chart-*
 * tokens, validated as a pair against the card surface.
 */

/** A CSS custom property's value, read once — SVG attributes cannot use var(). */
function useToken(name: string, fallback: string): string {
  const [value, setValue] = useState(fallback);
  useEffect(() => {
    const read = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    if (read) setValue(read);
  }, [name]);
  return value;
}

/** The recessive chrome every chart shares: hairline grid, muted ticks, card ring. */
function useChrome() {
  const border = useToken('--border', '214 32% 91%');
  const muted = useToken('--muted-foreground', '215 16% 47%');
  const card = useToken('--card', '0 0% 100%');
  const surface = useToken('--muted', '210 40% 98%');
  return {
    grid: `hsl(${border})`,
    tick: { fill: `hsl(${muted})`, fontSize: 11 },
    cursor: `hsl(${muted})`,
    card: `hsl(${card})`,
    wash: `hsl(${surface})`,
  };
}

// ── Revenue ──────────────────────────────────────────────────────────────────

export function RevenueChart({
  monthly,
  currency,
  showCollected,
}: {
  monthly: { month: string; label: string; invoiced: Money; collected: Money }[];
  currency: string;
  /** Collected is absent for somebody who cannot see payments. */
  showCollected: boolean;
}) {
  const invoicedColour = useToken('--chart-1', '#2a78d6');
  const collectedColour = useToken('--chart-2', '#eb6834');
  const chrome = useChrome();

  const data = monthly.map((row) => ({
    ...row,
    short: row.label.split(' ')[0],
    invoicedValue: toNumber(row.invoiced),
    collectedValue: toNumber(row.collected),
  }));

  const series = [
    { key: 'invoiced', label: 'Invoiced', colour: invoicedColour, total: sumMoney(monthly.map((row) => row.invoiced)) },
    ...(showCollected
      ? [{ key: 'collected', label: 'Collected', colour: collectedColour, total: sumMoney(monthly.map((row) => row.collected)) }]
      : []),
  ];

  return (
    <div>
      {/* A legend for two series, with each one's year total beside it — the
          one direct label worth the space on a twelve-month chart. */}
      <ul className="mb-3 flex flex-wrap gap-x-5 gap-y-1 text-xs">
        {series.map((item) => (
          <li key={item.key} className="flex items-center gap-1.5">
            <span className="h-2.5 w-2.5 rounded-sm" style={{ background: item.colour }} />
            <span className="text-muted-foreground">{item.label}</span>
            <span className="font-semibold" title={exactMoney(item.total, currency)}>
              {wholeMoney(item.total, currency)}
            </span>
          </li>
        ))}
      </ul>

      <div className="h-60 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} barGap={2} barCategoryGap="24%" margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
            <CartesianGrid vertical={false} stroke={chrome.grid} />
            <XAxis
              dataKey="short"
              tickLine={false}
              axisLine={{ stroke: chrome.grid }}
              tick={chrome.tick}
              // On a phone twelve month names do not fit; drop the ones that
              // would collide rather than overprint them.
              interval="preserveStartEnd"
              minTickGap={6}
            />
            <YAxis
              width={52}
              tickLine={false}
              axisLine={false}
              tick={chrome.tick}
              tickFormatter={(value: number) => compactMoney(value)}
            />
            <Tooltip
              cursor={{ fill: chrome.wash }}
              content={<MoneyTooltip currency={currency} rows={series} />}
            />
            <Bar
              dataKey="invoicedValue"
              name="invoiced"
              fill={invoicedColour}
              radius={[4, 4, 0, 0]}
              maxBarSize={24}
              isAnimationActive={false}
            />
            {showCollected && (
              <Bar
                dataKey="collectedValue"
                name="collected"
                fill={collectedColour}
                radius={[4, 4, 0, 0]}
                maxBarSize={24}
                isAnimationActive={false}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

/** The same twelve months as rows, for anyone who would rather read than hover. */
export function RevenueTable({
  monthly,
  currency,
  showCollected,
}: {
  monthly: { month: string; label: string; invoiced: Money; collected: Money }[];
  currency: string;
  showCollected: boolean;
}) {
  return (
    <div className="max-h-72 overflow-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-card text-xs text-muted-foreground">
          <tr className="border-b">
            <th className="py-2 text-start font-medium">Month</th>
            <th className="py-2 text-end font-medium">Invoiced</th>
            {showCollected && <th className="py-2 text-end font-medium">Collected</th>}
          </tr>
        </thead>
        <tbody>
          {[...monthly].reverse().map((row) => (
            <tr key={row.month} className="border-b last:border-0">
              <td className="py-1.5">{row.label}</td>
              <td className="tabular py-1.5 text-end">{exactMoney(row.invoiced, currency)}</td>
              {showCollected && (
                <td className="tabular py-1.5 text-end">{exactMoney(row.collected, currency)}</td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * One readout for every series at the hovered month. Values lead and the
 * series name follows, keyed by a short stroke of its colour.
 */
function MoneyTooltip({
  active,
  payload,
  label,
  currency,
  rows,
}: TooltipProps<number, string> & {
  currency: string;
  rows: { key: string; label: string; colour: string }[];
}) {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload as Record<string, Money | string>;

  return (
    <div className="rounded-lg border bg-popover px-3 py-2 text-xs shadow-md">
      <div className="mb-1.5 font-medium">{(point.label as string) ?? label}</div>
      {rows.map((row) => (
        <div key={row.key} className="flex items-center gap-2 py-0.5">
          <span className="h-0.5 w-3 rounded-full" style={{ background: row.colour }} />
          <span className="tabular font-semibold">{exactMoney(point[row.key] as Money, currency)}</span>
          <span className="text-muted-foreground">{row.label}</span>
        </div>
      ))}
    </div>
  );
}

// ── Forecast ─────────────────────────────────────────────────────────────────

/** "Week of 2026-09-14" → "14 Sep"; "Nov 2026" → "Nov". */
function shortBucket(label: string): string {
  const week = label.match(/^Week of (\d{4})-(\d{2})-(\d{2})$/);
  if (week) {
    const date = new Date(Number(week[1]), Number(week[2]) - 1, Number(week[3]));
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  }
  return label.split(' ')[0];
}

export function ForecastChart({
  forecast,
  currency,
}: {
  forecast: ForecastPeek;
  currency: string;
}) {
  const colour = useToken('--chart-1', '#2a78d6');
  const critical = useToken('--status-critical', '#d03b3b');
  const chrome = useChrome();

  const data = forecast.series.map((bucket) => ({
    ...bucket,
    short: shortBucket(bucket.label),
    closingValue: toNumber(bucket.closing),
  }));
  const dipsBelowZero = data.some((point) => point.closingValue < 0);

  return (
    <div className="h-48 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 6, right: 6, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} stroke={chrome.grid} />
          <XAxis
            dataKey="short"
            tickLine={false}
            axisLine={{ stroke: chrome.grid }}
            tick={chrome.tick}
            minTickGap={16}
          />
          <YAxis
            width={52}
            tickLine={false}
            axisLine={false}
            tick={chrome.tick}
            tickFormatter={(value: number) => compactMoney(value)}
          />
          {/* Zero only matters when the line crosses it, and then it is the
              point: a solid hairline in the critical colour, never dashed. */}
          {dipsBelowZero && <ReferenceLine y={0} stroke={critical} strokeWidth={1} />}
          <Tooltip
            cursor={{ stroke: chrome.cursor, strokeWidth: 1 }}
            content={<ForecastTooltip currency={currency} colour={colour} />}
          />
          <Area
            type="monotone"
            dataKey="closingValue"
            stroke={colour}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
            fill={colour}
            fillOpacity={0.1}
            dot={false}
            activeDot={{ r: 4, stroke: chrome.card, strokeWidth: 2, fill: colour }}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function ForecastTooltip({
  active,
  payload,
  currency,
  colour,
}: TooltipProps<number, string> & { currency: string; colour: string }) {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload as ForecastPeek['series'][number];

  return (
    <div className="rounded-lg border bg-popover px-3 py-2 text-xs shadow-md">
      <div className="mb-1.5 font-medium">{point.label}</div>
      <div className="flex items-center gap-2 py-0.5">
        <span className="h-0.5 w-3 rounded-full" style={{ background: colour }} />
        <span className="tabular font-semibold">{exactMoney(point.closing, currency)}</span>
        <span className="text-muted-foreground">Cash at the end</span>
      </div>
      <div className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 text-muted-foreground">
        <span>In</span>
        <span className="tabular text-end text-foreground">{exactMoney(point.inflow, currency)}</span>
        <span>Out</span>
        <span className="tabular text-end text-foreground">{exactMoney(point.outflow, currency)}</span>
      </div>
    </div>
  );
}

export function ForecastTable({ forecast, currency }: { forecast: ForecastPeek; currency: string }) {
  return (
    <div className="max-h-56 overflow-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-card text-xs text-muted-foreground">
          <tr className="border-b">
            <th className="py-2 text-start font-medium">Period</th>
            <th className="py-2 text-end font-medium">In ({currency})</th>
            <th className="py-2 text-end font-medium">Out ({currency})</th>
            <th className="py-2 text-end font-medium">Cash at end ({currency})</th>
          </tr>
        </thead>
        <tbody>
          {forecast.series.map((bucket) => (
            <tr key={bucket.key} className="border-b last:border-0">
              <td className="py-1.5">{bucket.label}</td>
              <td className="tabular py-1.5 text-end">{compactMoney(bucket.inflow)}</td>
              <td className="tabular py-1.5 text-end">{compactMoney(bucket.outflow)}</td>
              <td className="tabular py-1.5 text-end font-medium">{compactMoney(bucket.closing)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
